import { packagePrice, requiredFeeCents, totalCents } from "../../lib/proposalPresentation";
import { isCalendarDatePast } from '../../lib/calendarDate';
import { AcceptedOrder } from '../proposals/ProposalWorkflow';
import { AnnotatedPhoto } from '../../components/photos/AnnotatedPhoto';
/**
 * ProposalView - Public customer-facing proposal view with page-by-page navigation
 * Accessible without authentication via public token
 * Redesigned for mobile-first, presentation-style viewing
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  Checkbox,
  Input,
  Textarea,
  Sheet,
  IconButton,
  Modal,
  Table,
} from '@mui/joy';
import {
  CheckCircle,
  Phone,
  Envelope as Email,
  House as Home,
  DownloadSimple as Download,
  Star,
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
  FileText as Description,
  Images as PhotoLibrary,
  Ruler as Straighten,
  CurrencyDollar as AttachMoney,
  FilePdf as PictureAsPdf,
  X as Close,
} from '@phosphor-icons/react';
import { proposalsApi } from '../../lib/proposalsApi';
import type { PublicProposal, ProposalTier, PageType } from '../../types/proposals';
import '../../lib/pdfWorkerSetup';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Page type configuration
interface PageConfig {
  id: string;
  type: PageType | 'cover' | 'tier_good' | 'tier_better' | 'tier_best';
  title: string;
  icon: React.ReactNode;
  data?: any;
  introText?: string;
}

export default function ProposalView() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  // Native previews also supply the authenticated payload through this bridge.
  const isEmbedded = searchParams.get('embedded') === '1' && searchParams.get('preview') === '1';
  const isPreview = isEmbedded || searchParams.get('preview') === '1';
  const [embeddedProposal, setEmbeddedProposal] = useState<PublicProposal | undefined>();
  useEffect(() => {
    if (!isEmbedded) return;
    const receive = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.source === window.parent && event.data?.type === 'APEXOS_PROPOSAL_PREVIEW') setEmbeddedProposal(event.data.proposal);
    };
    window.addEventListener('message', receive);
    window.parent.postMessage({ type: 'APEXOS_PROPOSAL_PREVIEW_READY' }, window.location.origin);
    return () => window.removeEventListener('message', receive);
  }, [isEmbedded, token]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedTierId, setSelectedTierId] = useState<number | null>(null);
  const [selectedUpgrades, setSelectedUpgrades] = useState<number[]>([]);
  const [signatureName, setSignatureName] = useState('');
  const [comments, setComments] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [signature, setSignature] = useState<string>('');
  const [productSelections, setProductSelections] = useState<Record<string, number>>({});
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<number>(0);

  const { data: fetchedProposal, isLoading: queryLoading, error } = useQuery({
    queryKey: ['public-proposal', token, isPreview],
    queryFn: () => proposalsApi.getPublic(token!, { preview: isPreview }),
    enabled: !!token && !isEmbedded,
    refetchInterval: (query) => ['pending', 'processing'].includes(query.state.data?.acceptance_workflow?.state || '') ? 5000 : query.state.data?.acceptance_workflow?.state === 'error' ? 30000 : false,
  });

  const proposal = isEmbedded ? embeddedProposal : fetchedProposal;
  const isLoading = isEmbedded ? !embeddedProposal : queryLoading;

  const acceptMutation = useMutation({
    mutationFn: () => {
      if (isPreview) return Promise.reject(new Error('Signing is disabled in preview.'));
      return proposalsApi.accept(token!, {
        tier_id: selectedTierId!,
        signature: signature,
        name: signatureName,
        selected_upgrades: selectedUpgrades,
        product_selections: productSelections,
        comments: comments,
        offer_version: proposal?.project_offer?.version,
      });
    },
    onSuccess: () => {
      window.location.reload();
    },
  });

  // Build page list from proposal data
  const pages = useMemo((): PageConfig[] => {
    if (!proposal) return [];

    const pageList: PageConfig[] = [];

    // Cover page (always first)
    pageList.push({
      id: 'cover',
      type: 'cover',
      title: 'Cover',
      icon: <Home />,
    });

    // Add enabled pages from proposal
    const enabledPages = proposal.pages?.filter((p) => p.is_enabled) || [];

    enabledPages.forEach((page) => {
      // Skip title page (handled by cover) and authorization (handled separately)
      if (page.page_type === 'title') return;
      if (page.page_type === 'authorization') return;
      if (page.page_type === 'terms') return;

      if (page.page_type === 'introduction') {
        pageList.push({
          id: `page-${page.id}`,
          type: 'introduction',
          title: page.title || 'Introduction',
          icon: <Description />,
          introText: page.intro_text,
        });
      } else if (page.page_type === 'inspection') {
        pageList.push({
          id: `page-${page.id}`,
          type: 'inspection',
          title: page.title || 'Inspection',
          icon: <PhotoLibrary />,
          introText: page.intro_text,
        });
      } else if (page.page_type === 'measurements') {
        pageList.push({
          id: `page-${page.id}`,
          type: 'measurements',
          title: page.title || 'Measurements',
          icon: <Straighten />,
          introText: page.intro_text,
        });
      } else if (page.page_type === 'quote_details') {
        // Add individual tier pages
        const sortedTiers = [...(proposal.tiers || [])].sort((a, b) => a.sort_order - b.sort_order);
        sortedTiers.forEach((tier) => {
          pageList.push({
            id: `tier-${tier.id}`,
            type: `tier_${tier.tier_type}` as PageConfig['type'],
            title: `${tier.name} Option`,
            icon: <AttachMoney />,
            data: tier,
          });
        });
      } else if (page.page_type === 'custom') {
        pageList.push({
          id: `page-${page.id}`,
          type: 'custom',
          title: page.title || 'Additional Information',
          icon: <Description />,
          introText: page.intro_text,
        });
      } else if (
        page.page_type === 'manufacturer_marketing' ||
        page.page_type === 'manufacturer_other'
      ) {
        // Note: manufacturer_colors page removed - color/option selection now on Authorization page
        const mfr = proposal.featured_manufacturer_details;
        if (mfr) {
          let pdfUrl: string | null = null;
          let title = page.title;
          if (page.page_type === 'manufacturer_marketing' && mfr.general_marketing_material) {
            pdfUrl = mfr.general_marketing_material;
            title = title || 'Marketing Materials';
          } else if (page.page_type === 'manufacturer_other' && mfr.other_marketing_assets) {
            pdfUrl = mfr.other_marketing_assets;
            title = title || 'Additional Materials';
          }
          if (pdfUrl) {
            pageList.push({
              id: `page-${page.id}`,
              type: page.page_type,
              title,
              icon: <PictureAsPdf />,
              data: { pdfUrl, manufacturerName: mfr.name },
              introText: page.intro_text,
            });
          }
        }
      }
    });

    // Authorization page (always last)
    pageList.push({
      id: 'authorization',
      type: 'authorization',
      title: 'Authorization',
      icon: <CheckCircle />,
    });

    return pageList;
  }, [proposal]);

  const currentPage = pages[currentPageIndex];

  // Navigation handlers
  const goToPage = useCallback((index: number) => {
    if (index >= 0 && index < pages.length) {
      setCurrentPageIndex(index);
    }
  }, [pages.length]);

  const goNext = useCallback(() => {
    goToPage(currentPageIndex + 1);
  }, [currentPageIndex, goToPage]);

  const goPrev = useCallback(() => {
    goToPage(currentPageIndex - 1);
  }, [currentPageIndex, goToPage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        goNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        goPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPageIndex]);

  // Prevent iOS Safari swipe-back gesture on authorization page
  useEffect(() => {
    if (currentPage?.type === 'authorization') {
      document.body.style.overscrollBehaviorX = 'none';
      document.documentElement.style.overscrollBehaviorX = 'none';
      return () => {
        document.body.style.overscrollBehaviorX = '';
        document.documentElement.style.overscrollBehaviorX = '';
      };
    }
  }, [currentPage?.type]);

  // Touch swipe handling - disabled on authorization page to allow signature drawing
  const handleTouchStart = (e: React.TouchEvent) => {
    if (currentPage?.type === 'authorization') return;
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (currentPage?.type === 'authorization') return;

    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStartRef.current - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  // Canvas signature handling
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent iOS/Chrome swipe-back navigation
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    // Scale coordinates to account for canvas display size vs internal resolution
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent iOS/Chrome swipe-back navigation
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    // Scale coordinates to account for canvas display size vs internal resolution
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    // Only capture signature if user was actually drawing
    if (isDrawing) {
      const canvas = canvasRef.current;
      if (canvas) {
        setSignature(canvas.toDataURL());
      }
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature('');
  };

  const toggleUpgrade = (upgradeId: number) => {
    setSelectedUpgrades((prev) =>
      prev.includes(upgradeId) ? prev.filter((id) => id !== upgradeId) : [...prev, upgradeId]
    );
  };

  const calculateTotal = () => {
    if (!proposal || !selectedTierId) return 0;
    const tier = proposal.tiers.find((t) => t.id === selectedTierId);
    if (!tier) return 0;

    return totalCents(proposal, selectedTierId, selectedUpgrades) / 100;
  };

  // Get unique products that require option selection (deduplicated by product ID)
  const getProductsRequiringSelection = useCallback(() => {
    if (!proposal || !selectedTierId) return [];
    const tier = proposal.tiers.find((t) => t.id === selectedTierId);
    if (!tier) return [];

    // Get unique products with active options
    const seenProductIds = new Set<number>();
    const uniqueProducts: Array<{
      productId: number;
      productName: string;
      options: NonNullable<typeof tier.line_items[0]['product_details']>['options'];
      lineItemIds: number[]; // Track which line items use this product
    }> = [];

    for (const item of tier.line_items) {
      if (item.product_details?.options && item.product_details.options.filter(o => o.is_active).length > 0) {
        const productId = item.product_details.id;
        if (!seenProductIds.has(productId)) {
          seenProductIds.add(productId);
          uniqueProducts.push({
            productId,
            productName: item.product_details.name,
            options: item.product_details.options,
            lineItemIds: [item.id],
          });
        } else {
          // Add this line item ID to the existing product entry
          const existing = uniqueProducts.find(p => p.productId === productId);
          if (existing) {
            existing.lineItemIds.push(item.id);
          }
        }
      }
    }
    return uniqueProducts;
  }, [proposal, selectedTierId]);

  const productsRequiringSelection = getProductsRequiringSelection();

  // Check if all required products have options selected (by product ID)
  const allProductsSelected = productsRequiringSelection.every(
    (product) => productSelections[product.productId.toString()]
  );

  const canSubmit = !!(selectedTierId && signatureName && signature && allProductsSelected);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.level1',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !proposal) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.level1',
          p: 3,
        }}
      >
        <Alert color="danger" sx={{ maxWidth: 500 }}>
          <Typography level="title-md">Proposal Not Found</Typography>
          <Typography level="body-sm">
            This proposal may have expired or the link is invalid.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Already accepted
  if (proposal.status === 'accepted') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.level1',
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              <CheckCircle size={64} color="var(--joy-palette-success-500)" style={{ marginBottom: 16 }} />
              <Typography level="h3" sx={{ mb: 1 }}>
                Proposal Accepted
              </Typography>
              <Typography level="body-lg" sx={{ color: 'text.secondary', mb: 3 }}>
                Thank you! This proposal was accepted on{' '}
                {proposal.acceptance_date && new Date(proposal.acceptance_date).toLocaleDateString()}.
              </Typography>
              <Typography sx={{ mb: 2 }}>Your selections are saved. Our team will contact you to coordinate the next steps.</Typography>
              {proposal.acceptance_workflow && <AcceptedOrder acceptance={proposal.acceptance_workflow} customer />}

            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  // Expired
  if (proposal.status === 'expired' || (isCalendarDatePast(proposal.valid_until))) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.level1',
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Alert color="warning" sx={{ textAlign: 'center', py: 4 }}>
            <Typography level="h4" sx={{ mb: 1 }}>
              Proposal Expired
            </Typography>
            <Typography level="body-md">
              This proposal has expired. Please contact us for an updated quote.
            </Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  const org = proposal.organization_details;

  return (
    <Box
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.level1',
        display: 'flex',
        flexDirection: 'column',
        // Prevent iOS Safari swipe-back gesture on authorization page
        ...(currentPage?.type === 'authorization' && {
          overscrollBehaviorX: 'none',
          touchAction: 'pan-y pinch-zoom',
        }),
      }}
    >
      {/* Main Content Area */}
      <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', pb: 10 }}>
        {/* Render current page */}
        {currentPage?.type === 'cover' && (
          <CoverPage
            proposal={proposal}
            org={org}
          />
        )}

        {currentPage?.type === 'introduction' && (
          <IntroductionPage
            proposal={proposal}
            org={org}
            introText={currentPage.introText}
          />
        )}

        {currentPage?.type === 'custom' && (
          <CustomPage
            title={currentPage.title}
            introText={currentPage.introText}
          />
        )}

        {currentPage?.type === 'inspection' && (
          <InspectionPage
            photos={proposal.inspection_photos}
            introText={currentPage.introText}
          />
        )}

        {currentPage?.type === 'measurements' && (
          <MeasurementsPage
            measurements={proposal.measurements}
            introText={currentPage.introText}
          />
        )}

        {(currentPage?.type === 'tier_good' ||
          currentPage?.type === 'tier_better' ||
          currentPage?.type === 'tier_best') && (
          <TierPage
            tier={currentPage.data}
            requiredFees={requiredFeeCents(proposal) / 100}
            formatCurrency={formatCurrency}
            showTotalSquares={proposal.show_total_squares}
            totalSquares={proposal.total_squares}
            taxEnabled={proposal.tax_enabled}
          />
        )}

        {(currentPage?.type === 'manufacturer_marketing' ||
          currentPage?.type === 'manufacturer_other') && (
          <ManufacturerMaterialPage
            pdfUrl={currentPage.data?.pdfUrl}
            manufacturerName={currentPage.data?.manufacturerName}
            title={currentPage.title}
            introText={currentPage.introText}
          />
        )}

        {currentPage?.type === 'authorization' && (
          <AuthorizationPage
            proposal={proposal}
            selectedTierId={selectedTierId}
            setSelectedTierId={setSelectedTierId}
            selectedUpgrades={selectedUpgrades}
            toggleUpgrade={toggleUpgrade}
            productSelections={productSelections}
            setProductSelections={setProductSelections}
            productsRequiringSelection={productsRequiringSelection}
            signatureName={signatureName}
            setSignatureName={setSignatureName}
            comments={comments}
            setComments={setComments}
            canvasRef={canvasRef}
            startDrawing={startDrawing}
            draw={draw}
            stopDrawing={stopDrawing}
            clearSignature={clearSignature}
            formatCurrency={formatCurrency}
            calculateTotal={calculateTotal}
            canSubmit={canSubmit && !isPreview}
            acceptMutation={acceptMutation}
          />
        )}
      </Box>

      {/* Bottom Navigation Bar */}
      <NavigationBar
        pages={pages}
        currentIndex={currentPageIndex}
        onNavigate={goToPage}
        onPrev={goPrev}
        onNext={goNext}
      />
    </Box>
  );
}

// ==================== Page Components ====================

// Cover Page Component
const CoverPage: React.FC<{
  proposal: PublicProposal;
  org: PublicProposal['organization_details'];
}> = ({ proposal, org }) => {
  const streetViewUrl = (proposal as any).street_view_url;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        textAlign: 'center',
      }}
    >
      {/* Logo */}
      {org.logo && (
        <img
          src={org.logo}
          alt={org.name}
          style={{ height: 60, objectFit: 'contain', marginBottom: 24 }}
        />
      )}

      {/* Property Image */}
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 600 },
          aspectRatio: '4/3',
          backgroundColor: 'background.level2',
          borderRadius: 'lg',
          overflow: 'hidden',
          mb: 4,
          boxShadow: 'lg',
        }}
      >
        {proposal.cover_image ? (
          <img
            src={proposal.cover_image}
            alt="Property"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : streetViewUrl ? (
          <img
            src={streetViewUrl}
            alt="Property"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Home size={80} color="var(--joy-palette-neutral-300)" />
          </Box>
        )}
      </Box>

      {/* Title */}
      <Typography level="h1" sx={{ mb: 2 }}>
        PROPOSAL
      </Typography>
      <Typography level="h3" sx={{ mb: 3, color: 'text.secondary' }}>
        {proposal.title || 'Roofing Estimate'}
      </Typography>

      {/* Customer Info */}
      <Divider sx={{ width: 100, my: 3 }} />
      <Typography level="body-sm" sx={{ color: 'text.tertiary', textTransform: 'uppercase', mb: 1 }}>
        Prepared For
      </Typography>
      {proposal.contact_name && (
        <Typography level="title-lg" sx={{ mb: 1 }}>
          {proposal.contact_name}
        </Typography>
      )}
      {proposal.opportunity_address && (
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          {proposal.opportunity_address}
        </Typography>
      )}

      {/* Date */}
      <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 4 }}>
        {new Date(`${proposal.proposal_date}T12:00:00`).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}
      </Typography>
    </Box>
  );
};

// Introduction Page Component
const IntroductionPage: React.FC<{
  proposal: PublicProposal;
  org: PublicProposal['organization_details'];
  introText?: string;
}> = ({ proposal, org, introText }) => {
  return (
    <Box sx={{ maxWidth: 950, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Company Header */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
        {org.logo && (
          <img src={org.logo} alt={org.name} style={{ height: 40, objectFit: 'contain' }} />
        )}
        <Box>
          <Typography level="title-md">{org.name}</Typography>
          <Stack direction="row" spacing={2} sx={{ color: 'text.secondary' }}>
            {org.phone && <Typography level="body-xs"><Phone style={{ marginRight: 4 }} />{org.phone}</Typography>}
            {org.email && <Typography level="body-xs"><Email style={{ marginRight: 4 }} />{org.email}</Typography>}
          </Stack>
        </Box>
      </Stack>

      {/* Page Intro Text */}
      {introText && (
        <Typography level="body-md" sx={{ color: 'text.secondary', mb: 3 }}>
          {introText}
        </Typography>
      )}

      {/* Introduction Text */}
      {proposal.introduction_text && (
        <Card variant="plain" sx={{ mb: 4 }}>
          <Typography level="body-lg" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
            {proposal.introduction_text}
          </Typography>
        </Card>
      )}

      {/* Scope of Work */}
      {proposal.scope_of_work && proposal.scope_of_work.length > 0 && (
        <>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Scope of Work
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <ol style={{ margin: 0, paddingLeft: 24 }}>
                {proposal.scope_of_work.map((item, index) => (
                  <li key={index} style={{ marginBottom: 8 }}>
                    <Typography level="body-md">{item}</Typography>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

// Inspection Photos Page
const CustomPage: React.FC<{
  title: string;
  introText?: string;
}> = ({ title, introText }) => (
  <Box sx={{ p: { xs: 2, sm: 3 } }}>
    <Typography level="h3" sx={{ mb: 2, textAlign: 'center' }}>
      {title}
    </Typography>
    {introText && (
      <Typography
        level="body-md"
        sx={{
          color: 'text.secondary',
          whiteSpace: 'pre-wrap',
          textAlign: 'center',
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        {introText}
      </Typography>
    )}
  </Box>
);

const InspectionPage: React.FC<{
  photos: PublicProposal['inspection_photos'];
  introText?: string;
}> = ({ photos, introText }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const activePhoto = lightboxIndex != null ? photos[lightboxIndex] : null;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(() => {
    setLightboxIndex((i) => (i == null ? i : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);
  const showNext = useCallback(() => {
    setLightboxIndex((i) => (i == null ? i : (i + 1) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (lightboxIndex == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft' && photos.length > 1) showPrev();
      else if (e.key === 'ArrowRight' && photos.length > 1) showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, photos.length, closeLightbox, showPrev, showNext]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography level="h3" sx={{ mb: 2, textAlign: 'center' }}>
        Inspection Photos
      </Typography>
      {introText && (
        <Typography level="body-md" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          {introText}
        </Typography>
      )}

      {photos.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
            maxWidth: 950,
            mx: 'auto',
          }}
        >
          {photos.map((photo, index) => (
            <Card key={photo.id} variant="outlined">
              {photo.photo_url && (
                <Box
                  onClick={() => setLightboxIndex(index)}
                  sx={{
                    aspectRatio: '4/3',
                    overflow: 'hidden',
                    borderBottom: photo.description ? '1px solid' : 'none',
                    borderColor: 'divider',
                    cursor: 'zoom-in',
                  }}
                >
                  <AnnotatedPhoto
                    annotations={photo.annotations}
                    showAnnotations
                    src={photo.photo_url}
                    alt={photo.description || 'Inspection photo'}
                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'contain' }}
                  />
                </Box>
              )}
              {(photo.description || photo.tags.length > 0) && (
                <CardContent sx={{ p: 1.5, gap: 1 }}>
                  {photo.description && (
                    <Typography level="body-sm">{photo.description}</Typography>
                  )}
                  {photo.tags.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {photo.tags.map((tag) => (
                        <Chip
                          key={tag.id}
                          size="sm"
                          variant="solid"
                          sx={{
                            backgroundColor: tag.color,
                            color: '#fff',
                            '--Chip-radius': '999px',
                          }}
                        >
                          {tag.name}
                        </Chip>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </Box>
      ) : (
        <Alert color="neutral" sx={{ maxWidth: 500, mx: 'auto' }}>
          No inspection photos available.
        </Alert>
      )}

      {activePhoto && (
        <Modal
          open
          onClose={closeLightbox}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box
            onClick={closeLightbox}
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0, 0, 0, 0.92)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2, sm: 4 },
            }}
          >
            <IconButton
              variant="plain"
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              sx={{
                position: 'absolute', top: 16, right: 16, color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <Close />
            </IconButton>

            {photos.length > 1 && (
              <>
                <IconButton
                  variant="plain"
                  size="lg"
                  onClick={(e) => { e.stopPropagation(); showPrev(); }}
                  sx={{
                    position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                    color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  variant="plain"
                  size="lg"
                  onClick={(e) => { e.stopPropagation(); showNext(); }}
                  sx={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </>
            )}

            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                maxWidth: '95vw',
                maxHeight: '95vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {photos.length > 1 && (
                <Typography level="body-sm" sx={{ color: 'white', opacity: 0.75 }}>
                  {(lightboxIndex ?? 0) + 1} of {photos.length}
                </Typography>
              )}

              {activePhoto.photo_url && (
                <AnnotatedPhoto
                  annotations={activePhoto.annotations}
                  showAnnotations
                  src={activePhoto.photo_url}
                  alt={activePhoto.description || 'Inspection photo'}
                  style={{
                    maxWidth: '95vw',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: 4,
                    userSelect: 'none',
                  }}
                />
              )}

              {(activePhoto.description || activePhoto.tags.length > 0) && (
                <Box sx={{ maxWidth: 720, textAlign: 'center', color: 'white' }}>
                  {activePhoto.description && (
                    <Typography level="body-md" sx={{ color: 'white', mb: activePhoto.tags.length > 0 ? 1.5 : 0 }}>
                      {activePhoto.description}
                    </Typography>
                  )}
                  {activePhoto.tags.length > 0 && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="center">
                      {activePhoto.tags.map((tag) => (
                        <Chip
                          key={tag.id}
                          size="sm"
                          variant="solid"
                          sx={{
                            backgroundColor: tag.color,
                            color: '#fff',
                            '--Chip-radius': '999px',
                          }}
                        >
                          {tag.name}
                        </Chip>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

// Measurements Page — renders inline PDFs (uploaded EagleView/QuickMeasure docs win)
// or, falling back, the native ApexOS measurements (satellite snapshot + linear totals).
const MeasurementsPage: React.FC<{
  measurements: PublicProposal['measurements'];
  introText?: string;
}> = ({ measurements, introText }) => {
  const hasPdfs = measurements?.pdfs?.length > 0;
  const native = measurements?.native ?? null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography level="h3" sx={{ mb: 2, textAlign: 'center' }}>
        Property Measurements
      </Typography>
      {introText && (
        <Typography level="body-md" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          {introText}
        </Typography>
      )}

      {hasPdfs ? (
        <Stack spacing={3} sx={{ maxWidth: 950, mx: 'auto' }}>
          {measurements.pdfs.map((pdf) => (
            <MeasurementPdfBlock key={pdf.id} pdf={pdf} />
          ))}
        </Stack>
      ) : native ? (
        <Box sx={{ maxWidth: 950, mx: 'auto' }}>
          <Typography level="body-sm" sx={{ mb: 1.5, textAlign: 'center', color: 'text.secondary' }}>
            Total Sections: {native.total_sections}
            {' | '}
            Total Area: {Math.round(native.total_area_sqft).toLocaleString()} sq ft
            {native.total_lines > 0 ? ` | Lines: ${native.total_lines}` : ''}
          </Typography>

          {native.static_map_url && (
            <Box
              component="img"
              src={native.static_map_url}
              alt="Roof measurements satellite view"
              sx={{
                width: '100%',
                display: 'block',
                borderRadius: 'sm',
                mb: 2,
              }}
            />
          )}

          {(native.linear_totals.length > 0 || native.eave_perimeter_ft > 0) && (
            <Card variant="outlined">
              <Typography level="title-md" sx={{ p: 2, pb: 0 }}>
                Linear Measurements
              </Typography>
              <Table
                stripe="even"
                sx={{
                  '& th': { textTransform: 'uppercase', fontSize: 'xs' },
                  '& td:last-child, & th:last-child': { textAlign: 'right' },
                }}
              >
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Total (ft)</th>
                  </tr>
                </thead>
                <tbody>
                  {native.linear_totals.map((row) => (
                    <tr key={row.type}>
                      <td>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 14,
                              height: 3,
                              backgroundColor: row.color,
                              borderRadius: 1,
                            }}
                          />
                          {row.label}
                        </Box>
                      </td>
                      <td>{row.total_ft.toFixed(1)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td><strong>Eave/Perimeter</strong></td>
                    <td><strong>{native.eave_perimeter_ft.toFixed(1)}</strong></td>
                  </tr>
                </tbody>
              </Table>
            </Card>
          )}
        </Box>
      ) : (
        <Alert color="neutral" sx={{ maxWidth: 500, mx: 'auto' }}>
          No measurement data available.
        </Alert>
      )}
    </Box>
  );
};

const MeasurementPdfBlock: React.FC<{
  pdf: PublicProposal['measurements']['pdfs'][number];
}> = ({ pdf }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loadError, setLoadError] = useState(false);

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: '#525659',
          borderRadius: 'md',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        {loadError ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert color="danger">Unable to load measurement PDF.</Alert>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<Download />}
              component="a"
              href={pdf.file_url}
              target="_blank"
              sx={{ mt: 2 }}
            >
              Download PDF
            </Button>
          </Box>
        ) : (
          <Document
            file={pdf.file_url}
            onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoadError(false); }}
            onLoadError={() => setLoadError(true)}
            loading={<CircularProgress sx={{ my: 4 }} />}
          >
            {numPages && Array.from(new Array(numPages), (_, index) => (
              <Box key={`${pdf.id}_page_${index + 1}`} sx={{ mb: 2 }}>
                <Page
                  pageNumber={index + 1}
                  width={Math.min(900, window.innerWidth - 48)}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Box>
            ))}
          </Document>
        )}
      </Box>
      {pdf.original_filename && (
        <Typography
          level="body-xs"
          sx={{ color: 'text.tertiary', textAlign: 'center', mt: 1 }}
        >
          {pdf.original_filename}
        </Typography>
      )}
    </Box>
  );
};

// Individual Tier Page (display only - selection happens on Authorization page)
const TierPage: React.FC<{
  tier: ProposalTier;
  requiredFees: number;
  formatCurrency: (value: number) => string;
  showTotalSquares?: boolean;
  totalSquares?: number | null;
  taxEnabled?: boolean;
}> = ({ tier, requiredFees, formatCurrency, showTotalSquares, totalSquares, taxEnabled }) => {
  // Calculate original price from product pricing to detect discounts
  const pricePerSquare = tier.manufacturer_product_details?.price_per_square
    ? parseFloat(tier.manufacturer_product_details.price_per_square)
    : null;
  const calculatedSubtotal = pricePerSquare && totalSquares
    ? pricePerSquare * totalSquares
    : null;
  const calculatedTotal = calculatedSubtotal && taxEnabled
    ? calculatedSubtotal + (calculatedSubtotal * (tier.tax_rate / 100))
    : calculatedSubtotal;

  // Determine if this is a discount (manual price lower than calculated)
  const isDiscounted = tier.use_manual_pricing &&
    calculatedTotal !== null &&
    tier.total_amount < calculatedTotal;

  // Calculate savings amount
  const savings = isDiscounted && calculatedTotal ? calculatedTotal - tier.total_amount : 0;

  return (
    <Box sx={{ maxWidth: 950, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Card
        variant="outlined"
        sx={{
          position: 'relative',
        }}
      >
        {tier.is_recommended && (
          <Chip
            size="sm"
            color="warning"
            variant="solid"
            startDecorator={<Star size={18} />}
            sx={{ position: 'absolute', top: -10, right: 16 }}
          >
            Recommended
          </Chip>
        )}

        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              level="body-sm"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 1,
                color: 'text.tertiary',
              }}
            >
              {tier.tier_type_label || tier.tier_type} Option
            </Typography>
            <Typography level="h2">{tier.name}</Typography>
          </Box>

          {tier.description && (
            <Typography level="body-md" sx={{ mb: 3, textAlign: 'center' }}>
              {tier.description}
            </Typography>
          )}

          {/* Advantages */}
          {tier.advantages && tier.advantages.length > 0 && (
            <Box sx={{ mb: 4 }}>
              {tier.advantages.map((adv, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <CheckCircle
                    size={18} color="var(--joy-palette-success-500)"
                  />
                  <Typography level="body-md">{adv}</Typography>
                </Box>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Line Items - What's Included */}
          {tier.line_items && tier.line_items.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {tier.line_items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography level="body-sm">{item.description}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Total */}
          <Box
            sx={{
              textAlign: 'center',
              p: 3,
              backgroundColor: 'background.level1',
              borderRadius: 'md',
            }}
          >
            <Typography level="body-sm" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              Total Investment
            </Typography>
            {isDiscounted && calculatedTotal && (
              <Typography
                level="h3"
                sx={{
                  textDecoration: 'line-through',
                  color: 'danger.500',
                  mb: 0.5,
                }}
              >
                {formatCurrency(Number(calculatedTotal) + requiredFees)}
              </Typography>
            )}
            <Typography level="h1">{formatCurrency(Number(tier.total_amount) + requiredFees)}</Typography>
            {isDiscounted && savings > 0 && (
              <Chip size="md" color="success" variant="soft" sx={{ mt: 1 }}>
                Save {formatCurrency(savings)}
              </Chip>
            )}
            {showTotalSquares && totalSquares && (
              <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
                {totalSquares} squares
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

// Manufacturer Material Page (PDF viewer using react-pdf)
const ManufacturerMaterialPage: React.FC<{
  pdfUrl: string;
  manufacturerName: string;
  title: string;
  introText?: string;
}> = ({ pdfUrl, manufacturerName, title, introText }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfLoadError, setPdfLoadError] = useState(false);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoadError(false);
  };

  const onDocumentLoadError = () => {
    setPdfLoadError(true);
  };

  return (
    <Box sx={{ maxWidth: 950, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography level="h4">{title}</Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          From {manufacturerName}
        </Typography>
        {introText && (
          <Typography level="body-md" sx={{ color: 'text.secondary', mt: 1, maxWidth: 600, mx: 'auto' }}>
            {introText}
          </Typography>
        )}
      </Box>

      <Box sx={{
        backgroundColor: '#525659',
        borderRadius: 'md',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: 400,
      }}>
        {pdfLoadError ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert color="danger">Unable to load PDF preview</Alert>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<Download />}
              component="a"
              href={pdfUrl}
              target="_blank"
              sx={{ mt: 2 }}
            >
              Download PDF
            </Button>
          </Box>
        ) : (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={<CircularProgress sx={{ my: 4 }} />}
          >
            {numPages && Array.from(new Array(numPages), (_, index) => (
              <Box key={`page_${index + 1}`} sx={{ mb: 2 }}>
                <Page
                  pageNumber={index + 1}
                  width={Math.min(900, window.innerWidth - 48)}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Box>
            ))}
          </Document>
        )}
      </Box>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          startDecorator={<Download />}
          component="a"
          href={pdfUrl}
          target="_blank"
        >
          Download PDF
        </Button>
      </Box>
    </Box>
  );
};

// Authorization Page (with signature)
const AuthorizationPage: React.FC<{
  proposal: PublicProposal;
  selectedTierId: number | null;
  setSelectedTierId: (id: number | null) => void;
  selectedUpgrades: number[];
  toggleUpgrade: (id: number) => void;
  productSelections: Record<string, number>;
  setProductSelections: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  productsRequiringSelection: Array<{
    productId: number;
    productName: string;
    options: NonNullable<import('../../types/proposals').ProposalLineItem['product_details']>['options'];
    lineItemIds: number[];
  }>;
  signatureName: string;
  setSignatureName: (name: string) => void;
  comments: string;
  setComments: (comments: string) => void;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  startDrawing: (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => void;
  draw: (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => void;
  stopDrawing: () => void;
  clearSignature: () => void;
  formatCurrency: (value: number) => string;
  calculateTotal: () => number;
  canSubmit: boolean;
  acceptMutation: ReturnType<typeof useMutation<any, any, void>>;
}> = ({
  proposal,
  selectedTierId,
  setSelectedTierId,
  selectedUpgrades,
  toggleUpgrade,
  productSelections,
  setProductSelections,
  productsRequiringSelection,
  signatureName,
  setSignatureName,
  comments,
  setComments,
  canvasRef,
  startDrawing,
  draw,
  stopDrawing,
  clearSignature,
  formatCurrency,
  calculateTotal,
  canSubmit,
  acceptMutation,
}) => {
  return (
    <Box sx={{ maxWidth: 950, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography level="h3" sx={{ mb: 3, textAlign: 'center' }}>
        Authorization
      </Typography>

      {/* Tier Selection Summary */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Select Your Option
          </Typography>
          <RadioGroup
            value={selectedTierId?.toString() || ''}
            onChange={(e) => setSelectedTierId(parseInt(e.target.value))}
          >
            <Stack spacing={1}>
              {proposal.tiers
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((tier) => {
                  // Calculate original price to detect discounts
                  const pricePerSquare = tier.manufacturer_product_details?.price_per_square
                    ? parseFloat(tier.manufacturer_product_details.price_per_square)
                    : null;
                  const calculatedSubtotal = pricePerSquare && proposal.total_squares
                    ? pricePerSquare * proposal.total_squares
                    : null;
                  const calculatedTotal = calculatedSubtotal && proposal.tax_enabled
                    ? calculatedSubtotal + (calculatedSubtotal * (tier.tax_rate / 100))
                    : calculatedSubtotal;
                  const isDiscounted = tier.use_manual_pricing &&
                    calculatedTotal !== null &&
                    tier.total_amount < calculatedTotal;
                  const savings = isDiscounted && calculatedTotal ? calculatedTotal - tier.total_amount : 0;

                  return (
                    <Box
                      key={tier.id}
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 1.5, sm: 2 },
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 'sm',
                        backgroundColor:
                          selectedTierId === tier.id ? 'primary.softBg' : 'background.level1',
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectedTierId(tier.id)}
                    >
                      <Radio value={tier.id.toString()} sx={{ mt: { xs: 0.5, sm: 0 } }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography level="body-md" fontWeight="md">
                            {tier.name}
                          </Typography>
                          {tier.is_recommended && (
                            <Chip size="sm" color="warning" variant="soft">
                              Recommended
                            </Chip>
                          )}
                          {isDiscounted && savings > 0 && (
                            <Chip size="sm" color="success" variant="soft">
                              Save {formatCurrency(savings)}
                            </Chip>
                          )}
                        </Stack>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        {isDiscounted && calculatedTotal && (
                          <Typography
                            level="body-sm"
                            sx={{
                              textDecoration: 'line-through',
                              color: 'danger.500',
                            }}
                          >
                            {formatCurrency(Number(calculatedTotal) + requiredFeeCents(proposal) / 100)}
                          </Typography>
                        )}
                        <Typography level="title-md">{formatCurrency(packagePrice(proposal, tier))}</Typography>
                        {proposal.show_total_squares && proposal.total_squares && (
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            {proposal.total_squares} squares
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
            </Stack>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Product Options Selection */}
      {selectedTierId && productsRequiringSelection.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Select Your Colors/Options
            </Typography>
            <Stack spacing={3}>
              {productsRequiringSelection.map((product) => {
                const activeOptions = product.options.filter(o => o.is_active);
                const selectedOptionId = productSelections[product.productId.toString()];

                return (
                  <Box key={product.productId}>
                    <Typography level="body-md" fontWeight="md" sx={{ mb: 1.5 }}>
                      {product.productName}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'repeat(2, 1fr)',
                          sm: 'repeat(3, 1fr)',
                          md: 'repeat(4, 1fr)',
                        },
                        gap: 1.5,
                      }}
                    >
                      {activeOptions.map((option) => {
                        const isSelected = selectedOptionId === option.id;
                        return (
                          <Box
                            key={option.id}
                            onClick={() => setProductSelections(prev => ({
                              ...prev,
                              [product.productId.toString()]: option.id
                            }))}
                            sx={{
                              p: 1.5,
                              borderRadius: 'sm',
                              border: '2px solid',
                              borderColor: isSelected ? 'primary.500' : 'divider',
                              backgroundColor: isSelected ? 'primary.softBg' : 'background.surface',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              '&:hover': {
                                borderColor: isSelected ? 'primary.500' : 'primary.300',
                              },
                            }}
                          >
                            {option.image_url && (
                              <Box
                                component="img"
                                src={option.image_url}
                                alt={option.title}
                                sx={{
                                  width: '100%',
                                  aspectRatio: '1/1',
                                  objectFit: 'cover',
                                  borderRadius: 'xs',
                                  mb: 1,
                                }}
                              />
                            )}
                            <Typography
                              level="body-sm"
                              fontWeight={isSelected ? 'lg' : 'md'}
                              sx={{ textAlign: 'center' }}
                            >
                              {option.title}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    {!selectedOptionId && (
                      <Typography level="body-xs" sx={{ color: 'danger.500', mt: 1 }}>
                        Please select an option
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Add-ons & Fees */}
      {proposal.optional_upgrades && proposal.optional_upgrades.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Add-ons & Fees
            </Typography>
            <Stack spacing={1}>
              {proposal.optional_upgrades.map((upgrade) => (
                <Box
                  key={upgrade.id}
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1.5, sm: 2 },
                    p: 1.5,
                    borderRadius: 'sm',
                    backgroundColor: selectedUpgrades.includes(upgrade.id)
                      ? 'primary.softBg'
                      : 'background.level1',
                    cursor: 'pointer',
                  }}
                  onClick={() => !upgrade.is_required && toggleUpgrade(upgrade.id)}
                >
                  {upgrade.is_required ? <Chip size="sm">Required</Chip> : <Checkbox checked={selectedUpgrades.includes(upgrade.id)} sx={{ mt: { xs: 0.5, sm: 0 } }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="body-md" fontWeight="md">
                      {upgrade.name}{upgrade.is_required ? " (Required)" : ""}
                    </Typography>
                    {upgrade.description && (
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {upgrade.description}
                      </Typography>
                    )}
                  </Box>
                  <Typography level="body-md" fontWeight="lg" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                    +{formatCurrency(upgrade.total_price)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Total */}
      {selectedTierId && (
        <Card variant="soft" color="primary" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography level="title-lg">Total Amount</Typography>
              <Typography level="h2">{formatCurrency(calculateTotal())}</Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Signature Section */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
            By signing below, I authorize the work as described above and agree to pay the total
            amount upon completion. I certify that I am the property owner or have authority to
            authorize this work.
          </Typography>

          <Stack spacing={3}>
            <Box>
              <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>
                Your Name
              </Typography>
              <Input
                size="lg"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Enter your full legal name"
              />
            </Box>

            <Box>
              <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>
                Comments or Questions (Optional)
              </Typography>
              <Textarea
                minRows={2}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments or questions..."
              />
            </Box>

            <Box>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
              >
                <Typography level="body-sm" fontWeight="md">
                  Signature
                </Typography>
                <Button size="sm" variant="plain" onClick={clearSignature}>
                  Clear
                </Button>
              </Box>
              <Sheet
                variant="outlined"
                sx={{
                  borderRadius: 'sm',
                  overflow: 'hidden',
                  overscrollBehavior: 'none',
                }}
              >
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  style={{
                    width: '100%',
                    height: 150,
                    cursor: 'crosshair',
                    touchAction: 'none',
                  }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </Sheet>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                Draw your signature above using mouse or touch
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Terms & Conditions */}
      {proposal.terms_and_conditions && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Terms & Conditions
            </Typography>
            <Typography
              level="body-sm"
              sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', maxHeight: 200, overflow: 'auto' }}
            >
              {proposal.terms_and_conditions}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Submit Button */}
      <Box sx={{ textAlign: 'center' }}>
        <Button
          size="lg"
          color="success"
          disabled={!canSubmit}
          onClick={() => acceptMutation.mutate()}
          loading={acceptMutation.isPending}
          sx={{ minWidth: 200 }}
        >
          Accept Proposal
        </Button>

        {!canSubmit && (
          <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 1 }}>
            {!selectedTierId
              ? 'Please select an option above'
              : productsRequiringSelection.length > 0 && !productsRequiringSelection.every(p => productSelections[p.productId.toString()])
                ? 'Please select colors/options for all products'
                : !signatureName
                  ? 'Please enter your name'
                  : 'Please sign above'}
          </Typography>
        )}

        {acceptMutation.isError && (
          <Alert color="danger" sx={{ mt: 2 }}>
            Failed to accept proposal. Please try again.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

// ==================== Navigation Components ====================

const NavigationBar: React.FC<{
  pages: PageConfig[];
  currentIndex: number;
  onNavigate: (index: number) => void;
  onPrev: () => void;
  onNext: () => void;
}> = ({ pages, currentIndex, onPrev, onNext }) => {
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === pages.length - 1;

  return (
    <>
      {/* Left Arrow - Fixed on left side of screen */}
      <IconButton
        variant="solid"
        color="primary"
        onClick={onPrev}
        disabled={isFirst}
        sx={{
          position: 'fixed',
          left: { xs: 8, sm: 16 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1000,
          width: { xs: 44, sm: 56 },
          height: { xs: 44, sm: 56 },
          borderRadius: '50%',
          boxShadow: 'lg',
          opacity: isFirst ? 0.3 : 0.85,
          transition: 'all 0.2s',
          '&:hover': {
            opacity: 1,
            transform: 'translateY(-50%) scale(1.05)',
          },
          '&:disabled': {
            opacity: 0.3,
            backgroundColor: 'neutral.400',
          },
        }}
      >
        <ChevronLeft size={32} />
      </IconButton>

      {/* Right Arrow - Fixed on right side of screen */}
      <IconButton
        variant="solid"
        color="primary"
        onClick={onNext}
        disabled={isLast}
        sx={{
          position: 'fixed',
          right: { xs: 8, sm: 16 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1000,
          width: { xs: 44, sm: 56 },
          height: { xs: 44, sm: 56 },
          borderRadius: '50%',
          boxShadow: 'lg',
          opacity: isLast ? 0.3 : 0.85,
          transition: 'all 0.2s',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '50%',
            border: '3px solid',
            borderColor: 'primary.400',
            animation: isLast ? 'none' : 'ripple 2s ease-out infinite',
          },
          '@keyframes ripple': {
            '0%': {
              transform: 'scale(1)',
              opacity: 0.6,
            },
            '100%': {
              transform: 'scale(1.8)',
              opacity: 0,
            },
          },
          '&:hover': {
            opacity: 1,
            transform: 'translateY(-50%) scale(1.05)',
            '&::before': {
              animation: 'none',
              opacity: 0,
            },
          },
          '&:disabled': {
            opacity: 0.3,
            backgroundColor: 'neutral.400',
            '&::before': {
              animation: 'none',
            },
          },
        }}
      >
        <ChevronRight size={32} />
      </IconButton>

      {/* Bottom Page Indicator */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          py: 1.5,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 999,
        }}
      >
        <Typography level="body-sm" fontWeight="md" sx={{ color: 'text.secondary' }}>
          Page {currentIndex + 1} of {pages.length}
        </Typography>
      </Box>
    </>
  );
};
